import React, { useEffect, useMemo, useRef, useState } from "react";

import { connect } from "react-redux";

// component functions
import * as ComponentFunctions from "./ChangeTypeOfConsumption.Functions";
import {
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    MessageBar,
    MessageBarBody,
    MessageBarTitle,
    Spinner,
} from "@fluentui/react-components";
import { BuildingRegular, CallRegular, HomeRegular, ArrowRightRegular } from "@fluentui/react-icons";

import './ChangeTypeOfConsumption.Styles.css';

// ─── VisitTypeEnum: IN_PERSON = 0, TELEHEALTH = 1, HOME_VISIT = 2 ────────────
// El icono acompana al texto, nunca lo reemplaza: la modalidad tiene que
// poder leerse sin interpretar el pictograma.
//
// Las descripciones estaban cruzadas respecto del enum: "Remoto" es HOME_VISIT
// —el profesional va al domicilio del paciente, no es a distancia— y
// "Telefónico" es TELEHEALTH, que cubre tanto el teléfono como la videollamada.
// De ahí que el icono de "Remoto" pase de video a casa.
const VISIT_TYPES = [
    { key: 0, label: 'Presencial', hint: 'En el centro',                 Icon: BuildingRegular },
    { key: 1, label: 'Telefónico', hint: 'Por teléfono o videollamada',  Icon: CallRegular },
    { key: 2, label: 'Remoto',     hint: 'Visita a domicilio',           Icon: HomeRegular },
] as const;

const typeLabel = (type: number | undefined) =>
    VISIT_TYPES.find(t => t.key === type)?.label ?? '–';

// ─────────────────────────────────────────────────────────────────────────────

const ChangeTypeOfConsumptionComponent = (props: ComponentFunctions.Props) => {

    const current  = props.Consumption?.currentConsumptionType ?? 0;
    const original = props.Consumption?.originalConsumptionType ?? 0;
    const changed  = current !== original;

    const [pendingType, setPendingType] = useState<number | null>(null);
    const wasChangingRef = useRef(false);

    // Hide confirmation once the operation completes (isChangingType: true→false).
    // Tambien cierra el dialogo cuando la operacion falla; el error queda
    // visible en la barra de abajo.
    useEffect(() => {
        if (wasChangingRef.current && !props.isChangingType) {
            setPendingType(null);
        }
        wasChangingRef.current = props.isChangingType;
    }, [props.isChangingType]);

    // ── Navegacion por teclado ───────────────────────────────────────────────
    // Radiogroup de activacion manual: las flechas mueven el foco y recien
    // Enter/Espacio abre la confirmacion. Mover el foco no puede disparar un
    // cambio que despues hay que deshacer.
    const currentIndex = useMemo(
        () => Math.max(0, VISIT_TYPES.findIndex(t => t.key === current)),
        [current]
    );
    const [focusedIndex, setFocusedIndex] = useState(currentIndex);
    const buttonRefs = useRef<Array<HTMLButtonElement | null>>([]);

    useEffect(() => {
        setFocusedIndex(currentIndex);
    }, [currentIndex]);

    const moveFocus = (nextIndex: number) => {
        const total = VISIT_TYPES.length;
        const index = (nextIndex + total) % total;
        setFocusedIndex(index);
        buttonRefs.current[index]?.focus();
    };

    const handleGroupKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        switch (e.key) {
            case 'ArrowRight':
            case 'ArrowDown':
                e.preventDefault();
                moveFocus(focusedIndex + 1);
                break;
            case 'ArrowLeft':
            case 'ArrowUp':
                e.preventDefault();
                moveFocus(focusedIndex - 1);
                break;
            case 'Home':
                e.preventDefault();
                moveFocus(0);
                break;
            case 'End':
                e.preventDefault();
                moveFocus(VISIT_TYPES.length - 1);
                break;
            default:
                break;
        }
    };

    const handleTypeClick = (key: number) => {
        if (key === current || props.isChangingType) return;
        setPendingType(key);
    };

    const handleConfirm = () => {
        if (pendingType !== null) {
            props.ChangeVisitType(props.ProtocolId, props.Consumption!.id, pendingType);
        }
    };

    // Sin consumo cargado no hay nada que elegir: se evita pintar el selector
    // sobre valores por defecto que no corresponden a ninguna prestacion.
    if (!props.Consumption) {
        return (
            <div className="visitTypeSelector">
                <div className="visitTypeSelector-loading">
                    <Spinner size="tiny" label="Cargando modalidad" />
                </div>
            </div>
        );
    }

    return (
        <div className="visitTypeSelector">
            <div className="visitTypeSelector-header">
                <span className="visitTypeSelector-title" id="visitTypeSelector-label">
                    Modalidad de la prestación
                </span>

                {/* El resumen dice de donde viene y en que quedo, sin obligar a
                    comparar las tarjetas entre si. */}
                {changed ? (
                    <span className="visitTypeSelector-summary visitTypeSelector-summary--changed">
                        <span className="visitTypeSelector-summary-from">{typeLabel(original)}</span>
                        <ArrowRightRegular />
                        <span className="visitTypeSelector-summary-to">{typeLabel(current)}</span>
                    </span>
                ) : (
                    <span className="visitTypeSelector-summary">Sin cambios</span>
                )}
            </div>

            <div
                className="visitTypeSelector-buttons"
                role="radiogroup"
                aria-labelledby="visitTypeSelector-label"
                onKeyDown={handleGroupKeyDown}
            >
                {VISIT_TYPES.map(({ key, label, hint, Icon }, index) => {
                    const isActive   = key === current;
                    const isOriginal = key === original && changed;
                    const isPending  = pendingType === key && props.isChangingType;

                    return (
                        <button
                            key={key}
                            ref={(el) => { buttonRefs.current[index] = el; }}
                            type="button"
                            role="radio"
                            aria-checked={isActive}
                            tabIndex={index === focusedIndex ? 0 : -1}
                            className={[
                                'visitTypeSelector-btn',
                                isActive   ? 'visitTypeSelector-btn--active'   : '',
                                isOriginal ? 'visitTypeSelector-btn--original' : '',
                            ].filter(Boolean).join(' ')}
                            onClick={() => handleTypeClick(key)}
                            onFocus={() => setFocusedIndex(index)}
                            disabled={props.isChangingType}
                            title={isActive ? `${label} · modalidad actual` : `Cambiar a ${label}`}
                        >
                            <span className="visitTypeSelector-btn-icon" aria-hidden="true">
                                {isPending ? <Spinner size="extra-tiny" /> : <Icon />}
                            </span>

                            <span className="visitTypeSelector-btn-label">{label}</span>
                            <span className="visitTypeSelector-btn-hint">{hint}</span>

                            {/* El estado va escrito, no librado al color del borde. */}
                            {isActive && <span className="visitTypeSelector-tag visitTypeSelector-tag--active">Actual</span>}
                            {isOriginal && <span className="visitTypeSelector-tag visitTypeSelector-tag--original">Original</span>}
                        </button>
                    );
                })}
            </div>

            {props.failChangingType && (
                <MessageBar intent="error" className="visitTypeSelector-error">
                    <MessageBarBody>
                        <MessageBarTitle>No se pudo cambiar la modalidad</MessageBarTitle>
                        {props.errorOnChangingType?.ErrorMessage ?? 'Intente nuevamente en unos instantes.'}
                    </MessageBarBody>
                </MessageBar>
            )}

            {/* Un solo paso de confirmacion: el click sobre la tarjeta abre el
                dialogo directamente, sin un boton intermedio que ademas movia
                el contenido de lugar al aparecer. */}
            <Dialog
                open={pendingType !== null}
                onOpenChange={(_, data) => { if (!data.open && !props.isChangingType) setPendingType(null); }}
            >
                <DialogSurface>
                    <DialogBody>
                        <DialogTitle>Cambiar la modalidad de la prestación</DialogTitle>
                        <DialogContent>
                            <p className="visitTypeSelector-dialogChange">
                                <span className="visitTypeSelector-summary-from">{typeLabel(current)}</span>
                                <ArrowRightRegular />
                                <span className="visitTypeSelector-summary-to">{typeLabel(pendingType ?? undefined)}</span>
                            </p>
                            <p className="visitTypeSelector-dialogNote">
                                Esta acción modifica la modalidad únicamente a efectos de la liquidación.
                                No se modifica la prestación en el módulo médico ni en la historia clínica.
                            </p>
                        </DialogContent>
                        <DialogActions>
                            <Button
                                appearance="primary"
                                onClick={handleConfirm}
                                disabled={props.isChangingType}
                                icon={props.isChangingType ? <Spinner size="tiny" /> : undefined}
                            >
                                {props.isChangingType ? 'Cambiando…' : 'Confirmar cambio'}
                            </Button>
                            <Button
                                appearance="secondary"
                                onClick={() => setPendingType(null)}
                                disabled={props.isChangingType}
                            >
                                Cancelar
                            </Button>
                        </DialogActions>
                    </DialogBody>
                </DialogSurface>
            </Dialog>
        </div>
    );
};

export default connect(
    ComponentFunctions.mapStateToProps,
    ComponentFunctions.mapDispatchToProps
)(ChangeTypeOfConsumptionComponent as any);
